import './LegalPage.css';

function PrivacyPolicy() {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <a href="/" className="legal-nav-brand">
            <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="Advanced Bowling Tracker" className="legal-nav-icon" />
            <span>Advanced Bowling Tracker</span>
          </a>
        </div>
      </nav>

      <div className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: August 2, 2026</p>

        <section>
          <h2>Introduction</h2>
          <p>
            Advanced Bowling Tracker ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our mobile applications (iOS and Android) and website (collectively, the "Service").
          </p>
          <p>
            By using the Service, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section>
          <h2>Information We Collect</h2>

          <h3>Account Information</h3>
          <p>When you create an account, we collect:</p>
          <ul>
            <li>Your name and display name</li>
            <li>Email address</li>
            <li>Profile photo (if provided)</li>
            <li>Date of birth (optional; you may add or remove it at any time in Settings)</li>
            <li>An age-range confirmation used to enable or disable social features (see <strong>Age Confirmation and Children's Privacy</strong> below). We store only the outcome of that confirmation: whether you are 13 or older, where the answer came from, and when it was recorded. We do not store a precise age from this confirmation.</li>
            <li>Authentication credentials (managed securely through Firebase Authentication or third-party sign-in providers such as Apple or Google)</li>
          </ul>

          <h3>Bowling Data</h3>
          <p>When you use the Service, we collect bowling-related data you provide, including:</p>
          <ul>
            <li>Game scores and frame-by-frame shot data</li>
            <li>League information and team details</li>
            <li>Bowling ball inventory and equipment details</li>
            <li>Practice drill results</li>
            <li>Oil pattern selections</li>
            <li>Bowling alley/center selections</li>
            <li>Game notes and session details</li>
          </ul>

          <h3>Location Data</h3>
          <p>With your permission, the app accesses your device's approximate and precise location to find and rank nearby bowling alleys, and&mdash;on supported platforms, if you opt in&mdash;to remind you when you arrive at a bowling alley. This location is used on your device for these features; we do not store your device's continuous location on our servers. The location of a bowling alley you select is saved with your session so your scores are tied to where you bowled. You can deny or revoke location access at any time in your device settings&mdash;alley search still works by name without it.</p>

          <h3>Social Data</h3>
          <p>If you use our social features, we collect:</p>
          <ul>
            <li>Friend connections and follow relationships</li>
            <li>Reactions and comments on shared scores</li>
            <li>Friend codes and invite information</li>
          </ul>
          <p>You can report content or another user, and block another user, from within the app; blocking hides that user's content from you.</p>

          <h3>Push Notifications</h3>
          <p>With your permission, we use Firebase Cloud Messaging to send notifications about social activity (new scores from people you follow, reactions, comments, new followers) and league reminders. To deliver these, a device push token is stored on your account. You can turn notifications off in the app's Settings or in your device settings at any time.</p>

          <h3>Device and Usage Data</h3>
          <p>We automatically collect certain technical information, including:</p>
          <ul>
            <li>Device type and operating system version</li>
            <li>App version</li>
            <li>General usage analytics (e.g., features used, session duration)</li>
            <li>Crash logs and diagnostic information (via Firebase Crashlytics), used solely to identify and fix app stability problems</li>
          </ul>

          <h3>Advertising and Attribution Data</h3>
          <p>To understand how users discover the Service and to measure the effectiveness of advertising campaigns:</p>
          <ul>
            <li><strong>Advertising identifier (IDFA):</strong> On iOS devices, we may collect the device's advertising identifier solely to attribute app installs to ad campaigns and measure ad effectiveness. This is only collected with your explicit permission via the iOS App Tracking Transparency prompt. You may decline at the prompt, and you may revoke at any time via iOS Settings &rarr; Privacy &amp; Security &rarr; Tracking.</li>
            <li><strong>SKAdNetwork postbacks:</strong> Whether or not you grant tracking permission, Apple's SKAdNetwork framework may send privacy-preserving install attribution signals to ad networks on your behalf. These postbacks contain aggregated, anonymized data and no personally identifiable information.</li>
            <li><strong>Advertising ID (Android):</strong> On Android devices, the Google Advertising ID may be collected by Google Analytics for Firebase and Google's advertising services to attribute app installs and measure ad effectiveness. It is used for install attribution and analytics only, and is not used to track you across other companies' apps and websites for advertising. You can reset or delete your advertising ID and opt out of ads personalization in Android Settings &rarr; Privacy &rarr; Ads (wording varies by device).</li>
          </ul>
        </section>

        <section>
          <h2>How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the Service</li>
            <li>Store and sync your bowling scores and statistics across devices</li>
            <li>Enable social features such as following friends and sharing scores</li>
            <li>Manage league tracking and team features</li>
            <li>Process Pro subscription purchases (handled through the Apple App Store on iOS or Google Play on Android)</li>
            <li>Send important service-related communications</li>
            <li>Analyze usage patterns to improve the app experience</li>
            <li>Measure the effectiveness of advertising campaigns that promote the Service</li>
          </ul>
        </section>

        <section>
          <h2>Data Storage and Security</h2>
          <p>
            Your data is stored securely using Google Firebase services, including Firebase Authentication and Cloud Firestore. Data is encrypted in transit and at rest. We implement industry-standard security measures to protect your information.
          </p>
          <p>
            Bowling data is stored locally on your device and synced to our cloud servers when an internet connection is available. This allows the app to function fully offline.
          </p>
        </section>

        <section>
          <h2>Data Sharing</h2>
          <p>We do not sell, rent, or trade your personal information to third parties. We may share your information only in the following circumstances:</p>
          <ul>
            <li><strong>With your consent:</strong> When you choose to share scores or connect with friends through the app's social features</li>
            <li><strong>Service providers:</strong> We use Firebase (Google) for authentication, data storage, and hosting. These providers are bound by their own privacy policies</li>
            <li><strong>Legal requirements:</strong> If required by law, regulation, or legal process</li>
          </ul>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <p>The Service uses the following third-party services:</p>
          <ul>
            <li><strong>Firebase Authentication:</strong> For secure sign-in (including Google Sign-In and Sign in with Apple)</li>
            <li><strong>Cloud Firestore:</strong> For data storage and synchronization</li>
            <li><strong>Firebase Cloud Messaging:</strong> For push notifications</li>
            <li><strong>Firebase Analytics (Google Analytics 4):</strong> For understanding feature usage, app performance, and audience characteristics. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
            <li><strong>Firebase Crashlytics:</strong> For crash reporting and app stability diagnostics. See <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
            <li><strong>Google advertising services, SKAdNetwork &amp; Google Play install referrer:</strong> For measuring the effectiveness of advertising campaigns that promote the Service, including app install attribution (iOS and Android).</li>
            <li><strong>PostHog:</strong> For product analytics that help us understand how features are used so we can improve them.</li>
            <li><strong>Apple App Store / Google Play:</strong> For processing Pro subscription payments</li>
          </ul>
          <p>Each of these services has its own privacy policy governing the use of your data.</p>
        </section>

        <section>
          <h2>Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li><strong>Access your data:</strong> View all your bowling data within the app</li>
            <li><strong>Update your information:</strong> Edit your profile and bowling data at any time</li>
            <li><strong>Delete your account:</strong> Delete your account and all associated data directly within the app, or by contacting us at <a href="mailto:support@advancedbowlingtracker.com">support@advancedbowlingtracker.com</a></li>
            <li><strong>Control social sharing:</strong> Choose what information is visible to friends and what remains private, and report or block other users</li>
            <li><strong>Opt out of analytics:</strong> Toggle analytics collection off in the app's Settings, or contact us</li>
            <li><strong>Control location:</strong> Grant or deny location access at any time in your device settings</li>
            <li><strong>Control ad tracking:</strong> On iOS, decline the App Tracking Transparency prompt or change it via Settings &rarr; Privacy &amp; Security &rarr; Tracking; on Android, reset your advertising ID or opt out of ads personalization via Settings &rarr; Privacy &rarr; Ads</li>
          </ul>
        </section>

        <section>
          <h2>Age Confirmation and Children's Privacy</h2>
          <p>The Service is not directed at children under the age of 13.</p>
          <p>
            <strong>Age confirmation.</strong> Because the Service includes social features, we ask you once to confirm whether you are 13 or older. We record only the result of that confirmation: whether you are 13 or older, where the answer came from, and when it was recorded. We store it with your account so you are not asked again on another device. We use it for one purpose, deciding whether social features may be enabled. It is not used for advertising, marketing, or profiling.
          </p>
          <p>
            <strong>If you indicate you are under 13</strong>, all social features are turned off and stay off: the activity feed, public profiles and user search, following other bowlers, comments and reactions, live score sharing and spectating, and publishing your scores. Your profile is also removed from user-search results. You can still use the Service to track your own scores, statistics, leagues, equipment, and practice, none of which is shared with other users. Social features also stay off if you decline to answer the age confirmation; you can answer it later in the app's Settings.
          </p>
          <p>
            <strong>Optional date of birth.</strong> You may add a date of birth to your profile. It is optional and you can remove it at any time. If a date of birth indicates you are under 13, social features are disabled as described above.
          </p>
          <p>
            We do not knowingly collect personal information from children under 13 beyond the age confirmation described here, which exists specifically to protect them. If you believe we have collected information from a child under 13, please contact us immediately at <a href="mailto:support@advancedbowlingtracker.com">support@advancedbowlingtracker.com</a> and we will delete it.
          </p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or your data, please contact us at:
          </p>
          <p>
            <a href="mailto:support@advancedbowlingtracker.com">support@advancedbowlingtracker.com</a>
          </p>
        </section>
      </div>

      <footer className="legal-footer">
        <p>&copy; {new Date().getFullYear()} Joseph Miller. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
